// AI Generated File
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { LinksRepoService } from '@client/app/services/api/links-repo/links-repo.service';
import { PageRepoService } from '@client/app/services/api/page-repo/page-repo.service';
import { CrawlsRepoService } from '@client/app/services/api/crawls-repo/crawls-repo.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { SessionService } from '@monsido/core/session/session.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { IconsService } from '@client/ng2/layout/icons/icons.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';

interface NotificationItem {
    link: string;
    linkParams?: any;
    faIcon: string;
    classColor: string;
    title: string;
    message: string;
    type: string | string[];
    status?: string;
    count?: number;
    loading?: boolean;
    affections?: number;
}

@Component({
    selector: 'inventory-dashboard',
    templateUrl: './dashboard.component.html',
    standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
    private linksRepoService = inject(LinksRepoService);
    private pageRepoService = inject(PageRepoService);
    private crawlsRepoService = inject(CrawlsRepoService);
    private dateTimeService = inject(DateTimeService);
    private sessionService = inject(SessionService);
    private translateService = inject(TranslateService);
    private iconsService = inject(IconsService);
    private pipeService = inject(PipeService);

    isLoading = true;
    allowActions = true;

    // Chart properties
    labels: string[] = [];
    data: any[] = [];
    series: string[] = [];
    options: any = {};

    // Collection lists
    contentList: NotificationItem[] = [];
    technicalList: NotificationItem[] = [];
    linksList: NotificationItem[] = [];

    ngOnInit (): void {
        this.activate();
    }

    ngOnDestroy (): void {
        this.allowActions = false;
    }

    private async activate (): Promise<void> {
        this.allowActions = true;
        this.isLoading = true;

        try {
            await Promise.all([
                this.setupChart(),
                this.setupCollections(),
            ]);
        } finally {
            this.isLoading = false;
        }
    }

    private async setupChart (): Promise<void> {
        this.series = [
            this.translateService.getString('Crawled pages'),
            this.translateService.getString('Images'),
            this.translateService.getString('Documents'),
        ];

        this.options = {
            scales: {
                xAxes: [{
                    gridLines: { display: false },
                }],
                yAxes: [
                    {
                        id: this.series[0],
                        type: 'linear',
                        display: true,
                        position: 'left',
                        ticks: {
                            beginAtZero: true,
                            suggestedMax: 4,
                            maxTicksLimit: 5,
                            min: 0,
                            callback: (value) => this.pipeService.get('monNumber')(value),
                        },
                    },
                    {
                        id: this.series[1],
                        type: 'linear',
                        display: false,
                        position: 'left',
                        ticks: { beginAtZero: true, suggestedMax: 4, maxTicksLimit: 5 },
                    },
                    {
                        id: this.series[2],
                        type: 'linear',
                        display: false,
                        position: 'left',
                        ticks: { beginAtZero: true, suggestedMax: 4, maxTicksLimit: 5 },
                    },
                ],
            },
            legend: {
                display: true,
                position: 'bottom',
                labels: { boxWidth: 15, padding: 15, fontSize: 11 },
            },
            tooltips: {
                enabled: true,
                mode: 'x-axis',
                bodySpacing: 5,
                callbacks: {
                    label: (tooltipItem, data) => this.pipeService.get('monNumber')(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]),
                },
            },
            hover: {
                mode: 'x-axis',
            },
        };

        this.labels = [];
        this.data = [];

        const params = {
            page_size: 5,
        };

        try {
            const crawlData = await this.crawlsRepoService.getAll(params);
            if (!this.allowActions) {
                return;
            }

            const plainData = crawlData.reverse();
            const arr1: number[] = [];
            const arr2: number[] = [];
            const arr3: number[] = [];

            for (let k = 0; k < plainData.length; k++) {
                this.labels.push(this.dateTimeService.format(crawlData[k].date, 'MMM DD'));
                arr1.push(plainData[k].page_count);
                arr2.push(plainData[k].images_count);
                arr3.push(plainData[k].documents_count);
            }

            this.data.push(arr1);
            this.data.push(arr2);
            this.data.push(arr3);
        } catch (error) {
            // Handle error silently like the original angular.noop
        }
    }

    private async setupCollections (): Promise<void> {
        const documentScan = this.sessionService.domain?.scan?.scan_documents;

        this.contentList = [
            {
                link: 'base.customer.domain.inventory.content.pages',
                faIcon: this.iconsService.getIcon(['SIDEMENUES', 'INVENTORY', 'CONTENT_HTML']) as string,
                classColor: 'text-link',
                title: this.translateService.getString('HTML Pages'),
                message: this.translateService.getString('No HTML Pages found'),
                type: 'html',
            },
            {
                link: 'base.customer.domain.inventory.content.documents',
                faIcon: this.iconsService.getIcon(['SIDEMENUES', 'INVENTORY', 'CONTENT_DOCUMENTS']) as string,
                classColor: 'text-link',
                title: this.translateService.getString('Documents'),
                message: this.translateService.getString('No Documents found'),
                type: documentScan ? ['pdf', 'document'] : 'documents',
                status: 'active',
            },
        ];

        this.technicalList = [
            {
                link: 'base.customer.domain.inventory.content.css',
                faIcon: this.iconsService.getIcon(['SIDEMENUES', 'INVENTORY', 'TECHNICAL_CSS']) as string,
                classColor: 'text-link',
                title: this.translateService.getString('CSS'),
                message: this.translateService.getString('No CSS'),
                type: 'css',
            },
            {
                link: 'base.customer.domain.inventory.content.js',
                faIcon: this.iconsService.getIcon(['SIDEMENUES', 'INVENTORY', 'TECHNICAL_JS']) as string,
                classColor: 'text-link',
                title: this.translateService.getString('Javascript'),
                message: this.translateService.getString('No javascript'),
                type: 'js',
            },
            {
                link: 'base.customer.domain.inventory.content.frame',
                faIcon: this.iconsService.getIcon(['INVENTORY', 'DASHBOARD', 'CODE']) as string,
                classColor: 'text-link',
                title: this.translateService.getString('Frames'),
                message: this.translateService.getString('No Frames'),
                type: 'frame',
            },
            {
                link: 'base.customer.domain.inventory.content.iframe',
                faIcon: this.iconsService.getIcon(['INVENTORY', 'DASHBOARD', 'CODE']) as string,
                classColor: 'text-link',
                title: this.translateService.getString('IFrames'),
                message: this.translateService.getString('No IFrames'),
                type: 'iframe',
            },
        ];

        this.linksList = [
            {
                link: 'base.customer.domain.inventory.content.images',
                faIcon: this.iconsService.getIcon(['SIDEMENUES', 'INVENTORY', 'CONTENT_IMAGES']) as string,
                classColor: 'text-link',
                title: this.translateService.getString('Image Links'),
                message: this.translateService.getString('No Image Links found'),
                type: 'image',
            },
            {
                link: 'base.customer.domain.inventory.content.links',
                faIcon: this.iconsService.getIcon(['SIDEMENUES', 'INVENTORY', 'CONTENT_LINKS']) as string,
                classColor: 'text-link',
                title: this.translateService.getString('Content Links'),
                message: this.translateService.getString('No Content links'),
                type: ['link', 'spreadsheet', 'pdf', 'archive', 'document', 'file'],
            },
            {
                link: 'base.customer.domain.inventory.content.links',
                linkParams: { 'doc-target': 'spreadsheet' },
                faIcon: this.iconsService.getIcon(['INVENTORY', 'DASHBOARD', 'SPREADSHEET']) as string,
                classColor: 'text-link',
                title: this.translateService.getString('Spreadsheet links'),
                message: this.translateService.getString('No Spreadsheet links found'),
                type: 'spreadsheet',
            },
            {
                link: 'base.customer.domain.inventory.content.links',
                linkParams: { 'doc-target': 'document' },
                faIcon: this.iconsService.getIcon(['INVENTORY', 'DASHBOARD', 'WORD']) as string,
                classColor: 'text-link',
                title: this.translateService.getString('Word links'),
                message: this.translateService.getString('No Word links found'),
                type: 'document',
            },
            {
                link: 'base.customer.domain.inventory.content.links',
                linkParams: { 'doc-target': 'slide' },
                faIcon: this.iconsService.getIcon(['TABS', 'PAGES', 'SLIDE']) as string,
                classColor: 'text-link',
                title: this.translateService.getString('Slide links'),
                message: this.translateService.getString('No slide links found'),
                type: 'slide',
            },
        ];

        await this.loadCollectionData();
    }

    private async loadCollectionData (): Promise<void> {
        const documentScan = this.sessionService.domain?.scan?.scan_documents;

        // Load content list data
        for (const content of this.contentList) {
            if (!this.allowActions) {
                break;
            }

            try {
                if (!documentScan && content.link === 'base.customer.domain.inventory.content.documents') {
                    await this.getLinkCount(content);
                } else {
                    await this.getPageCount(content);
                }
                content.loading = false;
            } catch (error) {
                content.loading = false;
            }
        }

        // Load technical list data
        for (const technical of this.technicalList) {
            if (!this.allowActions) {
                break;
            }

            try {
                await this.getLinkCount(technical);
            } catch (error) {
            }
        }

        // Load links list data
        for (const link of this.linksList) {
            if (!this.allowActions) {
                break;
            }

            try {
                await this.getLinkCount(link);
            } catch (error) {
            }
        }
    }

    private async getPageCount (item: NotificationItem): Promise<void> {
        const params: any = {
            page_size: 1,
        };

        if (Array.isArray(item.type)) {
            params['types[]'] = item.type;
        } else {
            params.type = item.type;
        }

        item.count = 0;

        try {
            const pages = await this.pageRepoService.getAll(params);
            item.count = pages.total || 0;
        } catch (error) {
            // Handle error silently like the original angular.noop
        }
    }

    private async getLinkCount (item: NotificationItem): Promise<void> {
        const params: any = {
            page_size: 1,
            page_fixed: false,
            status: item.status || null,
        };

        item.count = 0;

        if (!Array.isArray(item.type)) {
            params.type = item.type;
            try {
                const links = await this.getLinks(params);
                item.count = links.total || 0;
            } catch (error) {
                // Handle error silently like the original angular.noop
            }
        } else {
            // Handle array of types
            for (const type of item.type) {
                if (!this.allowActions) {
                    break;
                }

                try {
                    const typeParams = { ...params, type: type };
                    const links = await this.getLinks(typeParams);
                    item.count = (item.count || 0) + (links.total || 0);
                } catch (error) {
                    // Handle error silently like the original angular.noop
                }
            }
        }
    }

    private async getLinks (params: any): Promise<any> {
        return this.linksRepoService.getAll(params);
    }
}
